import {
  afterNextRender,
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  inject,
  Injector,
  Input,
  OnChanges,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ButtonModule,
  IconModule,
  SvgIconRegistryService,
  TourGuideService,
} from '@hglobal/common';
import { environment } from '@fb/environments';

import { FloatingButtonComponent } from './components/floating-button/floating-button.component';
import { AuthService } from './services/auth/auth.service';
import { GrowthBookService } from './services/growth-book/growth-book.service';
import {
  FloatingButtonAction,
  FloatingButtonActionType,
  FloatingButtonConfig,
  FloatingButtonEnvironmentOverrides,
  FloatingButtonUser,
} from './models';
import { TranslationPathService } from './services/translation/translation-path.service';
import { DictionariesService } from './services/dictionaries.service';
import { IndexedDBStorageService } from './services/indexed-db-storage.service';
import { PageContextService } from './components/cta';
import { TranslateInPipe } from './pipes/translate-in.pipe';
import { applyFloatingButtonEnvironmentOverrides } from './helpers/apply-floating-button-environment-overrides';
import { buildFloatingButtonIconSet } from './helpers/build-floating-button-icon-set';
import { parseFloatingButtonConfig } from './helpers/parse-floating-button-config';
import { parseFloatingButtonEnvironmentOverrides } from './helpers/parse-floating-button-environment-overrides';

@Component({
  selector: 'pu-app-component',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  imports: [
    FloatingButtonComponent,
    ButtonModule,
    TranslateModule,
    IconModule,
    TranslateInPipe,
  ],
  encapsulation: ViewEncapsulation.ShadowDom,
})
export class AppComponent implements OnChanges, AfterViewInit {
  @Input() user?: FloatingButtonUser | string | null;

  @Input() translationPath?: string;
  @Input() language?: string = 'en';
  @Input() config: FloatingButtonConfig;
  /** Host-driven current route; used to resolve contextual page CTAs. */
  @Input() currentPath?: string;
  @Input() environmentOverrides?: FloatingButtonEnvironmentOverrides | string;

  @ViewChild('shellContainer') shellContainerElementRef: ElementRef;
  @ViewChild('welcomeTemplate') welcomeTemplateRef: TemplateRef<any>;
  @ViewChild('openHrBotTemplate') openHrBotTemplateRef: TemplateRef<any>;

  #authService = inject(AuthService);
  #growthBookService = inject(GrowthBookService);
  #injector = inject(Injector);
  #translateService = inject(TranslateService);
  #translationPathService = inject(TranslationPathService);
  #dictionariesService = inject(DictionariesService);
  #iconsService = inject(SvgIconRegistryService);
  #tourGuideService = inject(TourGuideService);
  #indexedDBStorageService = inject(IndexedDBStorageService);
  #pageContextService = inject(PageContextService);

  popupOpened = false;
  blockBotButton = environment.blockAiChat;
  showFinanceSupport = environment.shortName === 'pt';

  readonly #defaultActions: FloatingButtonAction[] = [
    { type: FloatingButtonActionType.PlatformSupport },
    { type: FloatingButtonActionType.ItSupport },
    ...(this.showFinanceSupport ? [{ type: FloatingButtonActionType.FinanceSupport }] : []),
    { type: FloatingButtonActionType.EducationSupport },
    { type: FloatingButtonActionType.TrustLine },
    { type: FloatingButtonActionType.AiChat, isDisabled: this.blockBotButton },
  ];
  actions: FloatingButtonAction[] = this.#defaultActions;

  constructor() {
    afterNextRender(
      () => this.#syncUserAndGrowthBook(),
      { injector: this.#injector },
    );
  }

  @HostBinding('style.zIndex')
  get zIndex() {
    return this.popupOpened ? '1000' : '';
  }

  onPopupOpen(opened: boolean) {
    this.popupOpened = opened;
  }

  ngOnChanges({
    language,
    translationPath,
    user,
    config,
    currentPath,
    environmentOverrides,
  }: SimpleChanges): void {
    if (environmentOverrides) {
      this.#applyEnvironmentOverrides(environmentOverrides.currentValue);
    }

    if (currentPath) {
      this.#pageContextService.setPath(currentPath.currentValue ?? null);
    }

    if (language || translationPath) {
      const lang = language?.currentValue || this.language;
      this.#translateService.use(lang);

      this.#translationPathService.setTranslationPath(
        translationPath?.currentValue || this.translationPath,
      );
    }

    if (user !== undefined) {
      this.#syncUserAndGrowthBook();
    }

    if (config?.currentValue) {
      this.#handleConfigChange(config.currentValue);
    }
  }

  ngAfterViewInit(): void {
    this.#registerIconsIfReady();

    this.#showTourGuide();
  }

  #registerIconsIfReady(): void {
    const host = this.shellContainerElementRef?.nativeElement;

    if (!host) {
      return;
    }

    this.#iconsService.addIcons(buildFloatingButtonIconSet(this.config), host);
  }

  async #showTourGuide(): Promise<void> {
    if (!(await this.#isAlreadyShowed()) && !this.blockBotButton) {
      this.#tourGuideService.setSteps(
        [
          {
            anchorId: 'pu-floating-button__component',
            delay: 1000,
            content: this.welcomeTemplateRef,
            position: 'tc',
            offset: [0, -8],
          },
          {
            anchorId: 'pt-floating-button__ai-chat',
            delay: 500,
            content: this.openHrBotTemplateRef,
            position: ['lc', 'tc'],
            offset: [-8, -8],
          },
        ],
        this.shellContainerElementRef.nativeElement,
      );

      this.#tourGuideService.start();
    }
  }

  async #isAlreadyShowed(): Promise<boolean> {
    await this.#checkLocalStorageFlag();

    const flag = await this.#indexedDBStorageService.getFlag(
      'pu-fb-popularization',
    );

    if (flag) {
      return true;
    }

    await this.#indexedDBStorageService.setFlag('pu-fb-popularization', true);

    return false;
  }

  async #checkLocalStorageFlag(): Promise<void> {
    if (localStorage.getItem('pu-fb-popularization')) {
      await this.#indexedDBStorageService.setFlag('pu-fb-popularization', true);

      return;
    }
  }

  #syncUserAndGrowthBook(): void {
    this.#authService.setUser(this.user);
    void this.#growthBookService.syncAttributes();
  }

  #handleConfigChange(config: FloatingButtonConfig | string): void {
    const parsedConfig = parseFloatingButtonConfig(config);

    this.#dictionariesService.addTranslations(
      parsedConfig.translations,
      this.language || 'en',
    );

    const configActions = parsedConfig?.actions?.length
      ? parsedConfig.actions
      : this.#defaultActions;

    this.actions = parsedConfig?.hideAiChatAction
      ? configActions.filter(action => action.type !== FloatingButtonActionType.AiChat)
      : configActions;

    this.#registerIconsIfReady();
  }

  #applyEnvironmentOverrides(
    overrides: FloatingButtonEnvironmentOverrides | string | null | undefined,
  ): void {
    applyFloatingButtonEnvironmentOverrides(
      parseFloatingButtonEnvironmentOverrides(overrides),
    );
  }
}
